import { type Marker, MarkerClusterer } from '@googlemaps/markerclusterer';
import { AdvancedMarker, InfoWindow, Map, useMap } from '@vis.gl/react-google-maps';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useTranslation from '@hooks/useTranslation';

import type { IAssetTracking } from '@model/assetTracking';

import { selectActiveAssetInfo, useAssetTrackerActions } from '../../store/useAssetTrackerStore';
import style from './style.module.scss';

interface IProps {
  assetMarkers?: IAssetTracking[];
}

const GoogleMapMarkers: React.FC<IProps> = ({ assetMarkers }) => {
  const { t } = useTranslation();

  const map = useMap();

  const [markers, setMarkers] = useState<{ [key: string]: Marker }>({});

  const { setActiveAssetInfo } = useAssetTrackerActions();

  const activeAssetInfo = selectActiveAssetInfo();

  const clusterer: MarkerClusterer | null = useMemo(() => {
    if (!map) return null;

    return new MarkerClusterer({ map });
  }, [map]);

  useEffect(() => {
    if (!map) return;

    if (assetMarkers?.length) {
      const bounds = new google.maps.LatLngBounds();
      assetMarkers.forEach((asset) => {
        bounds.extend(asset.lastReport.location);
      });
      map.fitBounds(bounds);
    }
  }, [map]);

  useEffect(() => {
    if (!clusterer) return;

    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(markers));
  }, [clusterer, markers]);

  const setMarkerRef = useCallback((marker: Marker | null, key: number) => {
    if (marker && markers[key]) return;
    if (!marker && !markers[key]) return;

    setMarkers((prev) => {
      if ((marker && prev[key]) || (!marker && !prev[key])) return prev;

      if (marker) {
        return { ...prev, [key]: marker };
      } else {
        const { [key]: _, ...newMarkers } = markers;

        return newMarkers;
      }
    });
  }, []);

  const handleActiveAsset = useCallback(
    (asset: IAssetTracking) => {
      setActiveAssetInfo(activeAssetInfo ? null : asset);
    },
    [activeAssetInfo],
  );

  return (
    <>
      {assetMarkers?.map((asset) => (
        <AdvancedMarker
          key={asset.id}
          ref={(marker) => setMarkerRef(marker, asset.id)}
          position={asset.lastReport?.location}
          onClick={() => handleActiveAsset(asset)}
        >
          <div className={style.mapMarker} />
        </AdvancedMarker>
      ))}
      {activeAssetInfo && (
        <InfoWindow anchor={markers[activeAssetInfo.id]} headerDisabled={true} className={style.infoWindow}>
          <div>
            {t('Tracker')}: {activeAssetInfo.assetTag}
          </div>
          <div>
            {t('GPS coordinates')}: {activeAssetInfo.lastReport?.location?.lat},{' '}
            {activeAssetInfo.lastReport?.location?.lng}
          </div>
          <div>
            {t('Address')}: {activeAssetInfo.lastReport?.address.info}
          </div>
          <div>
            {t('Last update')}: {activeAssetInfo.lastReport?.humanDate}
          </div>
        </InfoWindow>
      )}
    </>
  );
};

export default GoogleMapMarkers;
